/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/
import { merge } from '@adobe-commerce/elsie/lib';
import { config } from '@/plp/api/initialize/initialize';
import { ProductSearchResult, SearchVariables } from '@/plp/data/models';
import { Product } from '@/plp/data/models/product';

import { transformProduct } from './product';


export function transformProductSearchResponse(data: any, searchVariables?: SearchVariables): ProductSearchResult {
  const productSearch = data?.productSearch;

  const transformedResponse: ProductSearchResult = {
    facets: transformFacets(productSearch?.facets || [], searchVariables),
    items: productSearch?.items.map((product: any) => transformProduct(product?.productView)) || [],
    pageInfo: {
      currentPage: productSearch?.page_info?.current_page || 1,
      totalPages: productSearch?.page_info?.total_pages || 1,
      totalItems: productSearch?.page_info?.total_items || 0,
      pageSize: productSearch?.page_info?.page_size || 10, //replace with default constant
    },
    totalCount: productSearch?.total_count || 0,
    metadata: {
      filterableAttributes: data?.attributeMetadata?.filterableInSearch || [],
      sortableAttributes: transformSortAttributes(data?.attributeMetadata?.sortable || [], searchVariables),
    }
  };
  return merge(
    transformedResponse, // default transformer
    config.getConfig().models?.ProductSearchResult?.transformer?.(data), // custom transformer
  );
}

export const transformSearchResultProducts = (searchResponse: { productSearch: { items: any[] } }): Product[] =>
  searchResponse?.productSearch?.items?.reduce<Product[]>((returnedProductList, productItem) => {
    if (!productItem?.productView) return returnedProductList; // Skip if no productView and product is null

    const transformed = transformProduct(productItem.productView);
    if (transformed) {
      returnedProductList.push(transformed);
    }

    return returnedProductList;
  }, []) || [];

function transformSortAttributes(sortableAttributes: any[] = [], searchVariables?: SearchVariables): any[] {
  if (!sortableAttributes || sortableAttributes.length === 0) {
    return [];
  }

  return sortableAttributes
    .filter((option) => {
      // Only include position attribute if there's a category filter
      if (option.attribute === 'position') {
        const hasCategoryFilter = searchVariables?.filter?.some(
          (filter) => filter.attribute === 'categoryPath'
        );
        return hasCategoryFilter;
      }

      // Include all other attributes
      return true;
    })
    .map((option) => ({
      ...option,
      bidirectional: option.attribute === 'price',
    }));
}

function transformFacets(facets: any[] = [], searchVariables?: SearchVariables): any[] {
  if (!facets || facets.length === 0) {
    return [];
  }

  const hasCategoryPathFilter = searchVariables?.filter?.some(
    (filter) => filter.attribute === 'categoryPath'
  );

  // if hasCategoryPathFilter, then we need to remove facets with attribute 'categories'
  if (hasCategoryPathFilter) {
    return facets.filter((facet) => facet.attribute !== 'categories');
  }

  return facets;
}
